import React, { useEffect, useState, useRef, useCallback } from "react";
import { hslToRgb, rgbToHsl, parseRgba, toRgba } from "../config/graphThemes";

// ── VN notes ─────────────────────────────────────────────────────────────────
// Canvas drawing code (hue ring, SL disc, thumb handles) is unchanged —
// these are algorithmic pixel operations, not styled elements.
//
// JSX surface changes:
//   - Color swatch border: keep border-white/20 (works on any swatch color)
//   - Value text color: light mode updated from rgba(0,0,0,0.4) →
//     rgba(100,116,139,0.65) which is mutedText light resolved
//   - font-mono retained: appropriate for rgba/hex value display

/* ─── ColorWheelPicker (HSL color wheel) ─── */
const ColorWheelPicker = ({ value, onChange, dark }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [hsl, setHsl] = useState(() => {
    const { r, g, b } = parseRgba(value);
    return rgbToHsl(r, g, b);
  });
  const [dragging, setDragging] = useState(null);
  const size = 180;
  const ringWidth = 22;
  const discRadius = size / 2 - ringWidth - 8;
  const cx = size / 2;
  const cy = size / 2;

  useEffect(() => {
    const { r, g, b } = parseRgba(value);
    const next = rgbToHsl(r, g, b);
    setHsl(next);
  }, [value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    for (let angle = 0; angle < 360; angle += 0.5) {
      const startAngle = ((angle - 1) * Math.PI) / 180;
      const endAngle = ((angle + 1) * Math.PI) / 180;
      ctx.beginPath();
      ctx.arc(cx, cy, size / 2 - ringWidth / 2, startAngle, endAngle);
      ctx.lineWidth = ringWidth;
      const { r, g, b } = hslToRgb(angle, 100, 50);
      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.stroke();
    }

    const imgData = ctx.createImageData(discRadius * 2, discRadius * 2);
    for (let py = 0; py < discRadius * 2; py++) {
      for (let px = 0; px < discRadius * 2; px++) {
        const dx = px - discRadius;
        const dy = py - discRadius;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= discRadius) {
          const sat = ((dx / discRadius + 1) / 2) * 100;
          const lit = (1 - (dy / discRadius + 1) / 2) * 100;
          const { r, g, b } = hslToRgb(hsl.h, sat, lit);
          const i = (py * discRadius * 2 + px) * 4;
          imgData.data[i] = r;
          imgData.data[i + 1] = g;
          imgData.data[i + 2] = b;
          imgData.data[i + 3] = 255;
        }
      }
    }

    const offscreen = document.createElement("canvas");
    offscreen.width = discRadius * 2;
    offscreen.height = discRadius * 2;
    const offCtx = offscreen.getContext("2d");
    offCtx.putImageData(imgData, 0, 0);

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, discRadius, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(offscreen, cx - discRadius, cy - discRadius);
    ctx.restore();

    // Disc separator ring — canvas-specific, keep as resolved colors
    ctx.beginPath();
    ctx.arc(cx, cy, discRadius + 1, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.strokeStyle = dark ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.2)";
    ctx.stroke();

    const hueAngle = (hsl.h * Math.PI) / 180;
    const hueR = size / 2 - ringWidth / 2;
    const hx = cx + hueR * Math.cos(hueAngle);
    const hy = cy + hueR * Math.sin(hueAngle);
    ctx.beginPath();
    ctx.arc(hx, hy, ringWidth / 2 - 2, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "white";
    ctx.stroke();
    const hueRgb = hslToRgb(hsl.h, 100, 50);
    ctx.fillStyle = `rgb(${hueRgb.r},${hueRgb.g},${hueRgb.b})`;
    ctx.fill();

    const slX = cx + ((hsl.s / 100) * 2 - 1) * discRadius;
    const slY = cy + (1 - (hsl.l / 100) * 2) * discRadius;
    ctx.beginPath();
    ctx.arc(slX, slY, 6, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "white";
    ctx.stroke();
    const selRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    ctx.fillStyle = `rgb(${selRgb.r},${selRgb.g},${selRgb.b})`;
    ctx.fill();
  }, [hsl, dark, size, ringWidth, discRadius, cx, cy]);

  const getCanvasPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const updateHue = (x, y) => {
    const angle = Math.atan2(y - cy, x - cx);
    const deg = ((angle * 180) / Math.PI + 360) % 360;
    const next = { ...hsl, h: Math.round(deg) };
    setHsl(next);
    const { r, g, b } = hslToRgb(next.h, next.s, next.l);
    onChange(toRgba(r, g, b));
  };

  const updateSL = (x, y) => {
    let dx = (x - cx) / discRadius;
    let dy = (y - cy) / discRadius;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 1) {
      dx /= dist;
      dy /= dist;
    }
    const sat = Math.round(((dx + 1) / 2) * 100);
    const lit = Math.round((1 - (dy + 1) / 2) * 100);
    const next = { ...hsl, s: sat, l: lit };
    setHsl(next);
    const { r, g, b } = hslToRgb(next.h, next.s, next.l);
    onChange(toRgba(r, g, b));
  };

  const handleStart = (e) => {
    e.preventDefault();
    const { x, y } = getCanvasPos(e);
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist >= size / 2 - ringWidth && dist <= size / 2) {
      setDragging("ring");
      updateHue(x, y);
    } else if (dist <= discRadius) {
      setDragging("disc");
      updateSL(x, y);
    }
  };

  const handleMove = useCallback(
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      if (dragging === "ring") updateHue(x, y);
      else updateSL(x, y);
    },
    [dragging], // eslint-disable-line
  );

  const handleEnd = useCallback(() => {
    if (dragging) setDragging(null);
  }, [dragging]);

  useEffect(() => {
    if (!dragging) return;
    const opts = { passive: false };
    window.addEventListener("mousemove", handleMove, opts);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, opts);
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [dragging, handleMove, handleEnd]);

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          cursor: dragging ? "grabbing" : "crosshair",
          touchAction: "none",
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      />

      {/* Color preview + value display */}
      <div className="flex items-center gap-2">
        {/* Swatch — white/20 border works universally on any swatch color */}
        <div
          className="h-5 w-5 rounded-full border border-white/20"
          style={{ background: value }}
        />
        <span
          className="font-mono text-[10px]"
          style={{
            // mutedText dark resolved / mutedText light resolved
            color: dark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.65)",
          }}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

export default ColorWheelPicker;
